import { useState } from 'react'
import moment from 'moment'
import { IDateRangePickerProps } from 'transfix-ui/components/DateRangePicker'
import { IDateRangePickerOption } from 'transfix-ui/components/DateRangePicker/types'
import { FORMATS } from 'constants/datetime'
import { TQuoteFilters } from '../../../types'

const DATE_RANGE_OPTIONS: IDateRangePickerProps['options'] = [
  {
    key: 'custom',
    label: 'Select range',
    type: 'calendar',
  },
  {
    key: 'today',
    label: 'Today',
    type: 'namedRange',
    dateRange: {
      startAt: moment().startOf('day').format(FORMATS.yearFirst),
    },
  },
  {
    key: 'last_three_days',
    label: 'In Last 3 Days',
    type: 'namedRange',
    dateRange: {
      startAt: moment().startOf('day').subtract(2, 'day').format(FORMATS.yearFirst),
      endAt: moment().startOf('day').format(FORMATS.yearFirst),
    },
  },
  {
    key: 'last_week',
    label: 'In Last 1 Week',
    type: 'namedRange',
    dateRange: {
      startAt: moment().startOf('day').subtract(1, 'week').format(FORMATS.yearFirst),
      endAt: moment().startOf('day').format(FORMATS.yearFirst),
    },
  },
  {
    key: 'last_month',
    label: 'In Last 1 Month',
    type: 'namedRange',
    dateRange: {
      startAt: moment().startOf('day').subtract(1, 'month').format(FORMATS.yearFirst),
      endAt: moment().startOf('day').format(FORMATS.yearFirst),
    },
  },
]

interface IUsePricedDateProps {
  quotesFilters: TQuoteFilters
  setQuotesFilters: React.Dispatch<React.SetStateAction<TQuoteFilters>>
}

const findDateOptionByRange = (startAt?: string, endAt?: string) => {
  const match = DATE_RANGE_OPTIONS.find(
    option => option.dateRange?.startAt === startAt && option.dateRange?.endAt === endAt
  )
  if (match) {
    return match
  }

  // If we can't find an exact match, consider the range custom:
  return DATE_RANGE_OPTIONS.find(option => option.key === 'custom') || null
}

export const usePricedDate = ({ quotesFilters, setQuotesFilters }: IUsePricedDateProps) => {
  const initialSelection =
    quotesFilters.createdStartDate || quotesFilters.createdEndDate
      ? findDateOptionByRange(quotesFilters.createdStartDate, quotesFilters.createdEndDate)
      : null
  const [currentSelection, setCurrentSelection] = useState<IDateRangePickerOption | null>(
    initialSelection
  )
  const setPricedDate = (value: IDateRangePickerOption | null) => {
    setCurrentSelection(value)
    const startAt = value?.dateRange?.startAt
    const endAt = value?.dateRange?.endAt
    if (startAt || endAt) {
      setQuotesFilters({ ...quotesFilters, createdStartDate: startAt, createdEndDate: endAt })
    } else {
      const updatedQuotes = { ...quotesFilters }
      delete updatedQuotes.createdEndDate
      delete updatedQuotes.createdStartDate
      setQuotesFilters({ ...updatedQuotes })
    }
  }
  return { dateRangeOptions: DATE_RANGE_OPTIONS, currentSelection, setPricedDate }
}
